import { Link } from 'react-router-dom';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';
import BackToTop from '../../../components/feature/BackToTop';
import { useSEO } from '../../../hooks/useSEO';
import { SITE_URL } from '../../../utils/siteConfig';

const productCategories = [
  { icon: 'ri-shopping-bag-3-line', label: 'Verpakkingen & drukwerk' },
  { icon: 'ri-t-shirt-line', label: 'Kleding & textiel' },
  { icon: 'ri-smartphone-line', label: 'Elektronica & gadgets' },
  { icon: 'ri-home-4-line', label: 'Woonaccessoires & interieur' },
  { icon: 'ri-basketball-line', label: 'Sport & outdoor' },
  { icon: 'ri-gift-line', label: 'Promotieartikelen & relatiegeschenken' },
  { icon: 'ri-tools-line', label: 'Gereedschap & materialen' },
  { icon: 'ri-leaf-line', label: 'Duurzame & eco-producten' },
  { icon: 'ri-car-line', label: 'Auto-accessoires' },
  { icon: 'ri-scissors-line', label: 'Beauty & verzorging' },
  { icon: 'ri-restaurant-line', label: 'Keuken & huishouden' },
  { icon: 'ri-building-line', label: 'Bouwmaterialen & industrie' },
];

const steps = [
  {
    number: '01',
    title: 'Vertel ons wat je zoekt',
    description: 'Omschrijf je product zo goed mogelijk — materiaal, afmeting, hoeveelheid, budget. Een foto of referentielink is ook welkom.',
  },
  {
    number: '02',
    title: 'Wij zoeken de juiste fabrikant',
    description: 'Ons netwerk in China stelt ons in staat vrijwel elk product te sourcen. Wij nemen contact op met relevante fabrikanten voor jou.',
  },
  {
    number: '03',
    title: 'Offerte binnen 24-48 uur',
    description: 'Je ontvangt een complete prijsindicatie, inclusief kwaliteitsopties en levertijden. Alle prijzen zijn ex. BTW.',
  },
  {
    number: '04',
    title: 'Van inkoop tot levering',
    description: 'Na akkoord regelen wij de productie, kwaliteitscontrole en DDP levering aan jouw deur. Van inkoop tot aftercare, volledig geregeld.',
  },
];

export default function OverigPage() {
  useSEO({
    title: 'Overig & Maatwerk | Vrijwel Elk Product Inkopen uit China | DUC',
    description: 'Staat uw product er niet bij? DUC sourcet vrijwel alles uit China — maatwerk, niche producten en speciale inkoopopdrachten. Gratis prijsindicatie binnen 24-48 uur.',
    keywords: 'overig inkopen China, maatwerk producten China, niche producten importeren, product sourcing op maat, DUC Direct Uit China',
    canonical: `${SITE_URL}/voor-wie/overig`,
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Voor wie', item: `${SITE_URL}/voor-wie` },
          { '@type': 'ListItem', position: 3, name: 'Overig & Maatwerk', item: `${SITE_URL}/voor-wie/overig` },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'Overig & Maatwerk — DUC sourcet vrijwel elk product uit China',
        description: 'Staat uw product er niet bij? DUC sourcet vrijwel alles uit China. Van niche producten tot complete maatwerkprojecten — gratis prijsindicatie.',
        url: `${SITE_URL}/voor-wie/overig`,
        inLanguage: 'nl-NL',
      },
    ],
  });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[52vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: 'url(https://public.readdy.ai/ai/img_res/e8925407313a4cfcd0573c0cc7086209.jpg)' }}
        />
        <div className="absolute inset-0 bg-black/70" />
        <div className="relative z-10 text-center px-5 max-w-3xl mx-auto pt-32 pb-16">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 mb-6">
            <i className="ri-question-line text-[#3B9EE8] text-sm" />
            <span className="text-white/80 text-xs font-500 tracking-wide">Overig & Maatwerk</span>
          </div>
          <h1 className="font-heading font-800 text-white text-4xl md:text-5xl leading-tight mb-5">
            Staat jouw product <br />
            <span className="text-[#3B9EE8]">er niet bij?</span>
          </h1>
          <p className="text-white/70 text-base md:text-lg leading-relaxed mb-8">
            Geen probleem. Wij kunnen vrijwel alles sourcen uit China. Of het nu een niche product is, een compleet maatwerkproject of iets wat je nergens anders vindt — vertel ons wat je zoekt.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/contact"
              className="bg-[#3B9EE8] hover:bg-[#2D85C5] text-white font-700 text-sm px-7 py-3.5 rounded-md transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              Vertel ons wat je zoekt →
            </Link>
            <a
              href="https://wa.me/31624359162?text=Hoi%20Shubam%2C%20ik%20zoek%20een%20product%20dat%20niet%20in%20jullie%20categorie%C3%ABn%20staat."
              target="_blank"
              rel="nofollow noreferrer"
              className="flex items-center gap-2 border border-white/30 hover:border-white/60 text-white/80 hover:text-white font-500 text-sm px-7 py-3.5 rounded-md transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-whatsapp-line text-base" />
              Direct WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* What we can source */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-5 md:px-6">
          <div className="text-center mb-12">
            <p className="text-[#3B9EE8] text-xs font-700 tracking-widest uppercase mb-3">Ons bereik</p>
            <h2 className="font-heading font-800 text-[#0A1628] text-3xl md:text-4xl mb-4">
              Wat kunnen wij sourcen?
            </h2>
            <p className="text-[#0A1628]/55 text-base max-w-2xl mx-auto leading-relaxed">
              Van standaard productcategorieën tot bijzondere maatwerkproducten — ons netwerk van fabrikanten in China dekt vrijwel elk product segment.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4 mb-12">
            {productCategories.map((cat) => (
              <div
                key={cat.label}
                className="flex items-center gap-3 p-3.5 rounded-xl border border-[#0A1628]/8 bg-[#F8F9FC] hover:border-[#3B9EE8]/30 hover:bg-[#3B9EE8]/4 transition-all duration-200"
              >
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#0A1628]/6 flex-shrink-0">
                  <i className={`${cat.icon} text-[#0A1628]/60 text-sm`} />
                </div>
                <span className="text-[#0A1628]/70 text-xs font-500 leading-snug">{cat.label}</span>
              </div>
            ))}
          </div>

          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-[#3B9EE8]/8 border border-[#3B9EE8]/20 rounded-full px-5 py-2.5">
              <i className="ri-infinity-line text-[#3B9EE8] text-sm" />
              <span className="text-[#3B9EE8] text-sm font-600">Staat het er niet tussen? Vraag het gewoon — wij kijken mee.</span>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 md:py-24 bg-[#F8F9FC]">
        <div className="max-w-5xl mx-auto px-5 md:px-6">
          <div className="text-center mb-12">
            <p className="text-[#3B9EE8] text-xs font-700 tracking-widest uppercase mb-3">Hoe het werkt</p>
            <h2 className="font-heading font-800 text-[#0A1628] text-3xl md:text-4xl mb-4">
              Van idee tot levering
            </h2>
            <p className="text-[#0A1628]/55 text-sm max-w-xl mx-auto">
              Vier stappen. Geen gedoe. Wij regelen alles.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {steps.map((step) => (
              <div key={step.number} className="bg-white rounded-xl p-6 border border-[#0A1628]/8">
                <div className="flex items-start gap-4">
                  <span className="text-[#3B9EE8]/30 font-800 text-3xl leading-none flex-shrink-0">{step.number}</span>
                  <div>
                    <h3 className="font-heading font-700 text-[#0A1628] text-base mb-2">{step.title}</h3>
                    <p className="text-[#0A1628]/55 text-sm leading-relaxed">{step.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* USP bar */}
      <section className="py-10 bg-[#0A1628]">
        <div className="max-w-5xl mx-auto px-5 md:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { icon: 'ri-search-eye-line', title: 'Alles sourcen', sub: 'Vrijwel elk product' },
              { icon: 'ri-timer-line', title: '24-48u reactie', sub: 'Offerte snel klaar' },
              { icon: 'ri-shield-check-line', title: 'Kwaliteitscheck', sub: 'Vóór verzending' },
              { icon: 'ri-truck-line', title: 'DDP levering', sub: 'Prijzen ex. BTW' },
            ].map((item) => (
              <div key={item.title} className="flex flex-col items-center gap-2">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#3B9EE8]/15">
                  <i className={`${item.icon} text-[#3B9EE8] text-lg`} />
                </div>
                <p className="text-white font-700 text-sm">{item.title}</p>
                <p className="text-white/40 text-xs">{item.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-2xl mx-auto px-5 md:px-6 text-center">
          <h2 className="font-heading font-800 text-[#0A1628] text-3xl md:text-4xl mb-4">
            Vertel ons wat je zoekt
          </h2>
          <p className="text-[#0A1628]/55 text-base leading-relaxed mb-8">
            Stuur ons een berichtje via WhatsApp of het contactformulier. Wij kijken mee en geven je binnen 24 uur een eerlijke prijsindicatie. Gratis, vrijblijvend en zonder verborgen kosten. Alle prijzen zijn ex. BTW.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/contact"
              className="bg-[#3B9EE8] hover:bg-[#2D85C5] text-white font-700 text-sm px-8 py-3.5 rounded-md transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              Gratis offerte aanvragen →
            </Link>
            <a
              href="https://wa.me/31624359162?text=Hoi%20Shubam%2C%20ik%20zoek%20een%20product%20dat%20niet%20in%20jullie%20categorie%C3%ABn%20staat."
              target="_blank"
              rel="nofollow noreferrer"
              className="flex items-center gap-2 border border-[#0A1628]/20 hover:border-[#3B9EE8]/50 text-[#0A1628]/65 hover:text-[#3B9EE8] font-500 text-sm px-8 py-3.5 rounded-md transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-whatsapp-line text-base" />
              WhatsApp Shubam
            </a>
          </div>
          <p className="text-[#0A1628]/30 text-xs mt-6 italic">&ldquo;Van inkoop tot aftercare, volledig geregeld&rdquo;</p>
        </div>
      </section>

      <Footer />
      <BackToTop />
    </div>
  );
}
